import type { Zone } from '@seek/audience-zones';
import type {
  Brand,
  CountrySiteName,
  Language,
  Locale,
} from '@seek/melways-sites';
import type { Country } from 'src/types/globals';

export const SMARTER_SEARCH_CLUSTERS = [
  'default',
  'ph-launch',
  'end-state-launch',
] as const;

export type SmarterSearchCluster = (typeof SMARTER_SEARCH_CLUSTERS)[number];

export interface DisplayDates {
  startDate?: string;
  endDate?: string;
}

export interface ZoneFeatures {
  QUICK_SEARCH_ENABLED?: boolean;
  LMIS_ENABLED?: boolean;
  NUDGE_ENABLED?: boolean;
  COMPANY_PROFILE_CAROUSEL?: boolean;
  SEARCH_PAGE_SIZE?: number;
  SPLITVIEW_ENABLED?: boolean;
  SALARY_TRANSPARENCY_ENABLED?: boolean;
  PDPA_CONSENT_REQUIRED?: boolean;
  SHOW_HOMEPAGE_BANNER?: boolean;
  HOMEPAGE_BANNER_TYPE?: string;
  SHOW_MARKETING_AD_SPLIT_VIEW?: boolean;
  SHOW_FLOATING_SAVE_SEARCH?: boolean;
  NORTHSTAR_SEO_TAGS?: boolean;
  SHOULD_OPEN_APPLY_IN_NEW_TAB?: boolean;
  SHOW_JOBCARD_TEASER_IN_SPLITVIEW?: boolean;
  AUTO_SELECT_SPLIT_VIEW_FIRST_JOB?: boolean;
  SHOW_STICKY_APPLY_ON_DESKTOP?: boolean;
  SERP_KEYWORD_TEXTFIELD?: boolean;
  COMPANY_PROFILE_ON_JDV?: boolean;
}

export type PrivacyPolicyFieldType = 'CookieConsent' | 'PrivacyPolicy';

export interface PrivacyPolicyField<T extends PrivacyPolicyFieldType> {
  displayDates: DisplayDates;
  type: T;
}

export interface PrivatePolicyBanner {
  cookieConsent?: PrivacyPolicyField<'CookieConsent'>;
  privatePolicy?: PrivacyPolicyField<'PrivacyPolicy'>;
}

export interface CandidateOnboardingBanner {
  displayDates: DisplayDates;
  externalHelpURL: {
    en: string;
    id?: string;
    th?: string;
  };
}

export interface ZoneConfig {
  zone: Zone;
  country: Country;
  brand: Brand;
  availableLocales: Locale[];
  defaultLocale: Locale;
  timedBanners: {
    privatePolicyBanner?: PrivatePolicyBanner;
    candidateOnboardingBanner?: CandidateOnboardingBanner;
  };
  zoneFeatures: ZoneFeatures;
  BRANCH_IO_KEY: string;
}

export interface AppConfig extends ZoneConfig {
  selectedLocale: Locale;
  site: CountrySiteName;
}

export interface SalaryRange_Frequency {
  label: string;
  type: string;
  isDefault: boolean;
}
export interface SalaryRange_Range {
  shortLabel: string;
  longLabel: string;
  value: string;
}
export interface SalaryRange_Currency {
  code: string;
}

export interface SalaryRange_SearchSalaryRange {
  frequency: SalaryRange_Frequency;
  range: SalaryRange_Range[];
}
export interface SalaryRange {
  defaultCurrency: SalaryRange_Currency;
  searchSalaryRanges: SalaryRange_SearchSalaryRange[];
}

export type AllSalaryRanges = {
  [key in Language]?: SalaryRange;
};

export interface ChildClassification {
  id: number;
  description: string;
}

export interface ParentClassification {
  id: number;
  description: string;
  subClassifications: ChildClassification[];
}

export type Classifications = ParentClassification[];

export type AllClassifications = {
  [key in Language]?: Classifications;
};

export type WorkTypes = {
  [key in Language]?: Array<{ label: string; value: string }>;
};

export type DateRanges = {
  [key in Language]?: Array<{ label: string; value: string }>;
};

export type Distances = {
  [key in Language]?: Array<{ label: string; value: string }>;
};
